import React from "react";
import { FeedFollowCtaWithDetails } from "web/react/components/feed-follow-cta-with-details";
import { FeedHeaderImage } from "web/react/components/feed-header-image";
import { PromotionHeader } from "web/react/components/promotion-header";
import { StorefrontDescription } from "web/react/components/storefront-description/";
import { StorefrontInfoList } from "web/react/components/storefront-modal/storefront-info-list/";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { Sticker } from "web/react/emo/sticker";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { VerifiedBadge } from "web/react/emo/verified-badge";
import { gettext } from "web/script/modules/django-i18n";
import { canUseMembership } from "web/script/modules/utils";
import { FollowButtonSerializer, RetailerPromotionsSerializer } from "web/types/serializers";

export interface StorefrontHeaderSummaryProps {
    retailerName: string;
    description: string | null;
    photoUrl: string | null;
    smallPhotoUrl: string | null;
    followButton: FollowButtonSerializer | null;
    followersCountLabel: string | null;
    dateAdded: string | null;
    shipsToCountry: string | null;
    bestShippingOption: string | null;
    promotions?: RetailerPromotionsSerializer | null;
    isCheckout?: boolean;
    isOfficialStore?: boolean;
    onClick: () => void;
}

export function StorefrontHeaderSummary({
    retailerName,
    description,
    photoUrl,
    smallPhotoUrl,
    followButton,
    followersCountLabel,
    promotions,
    dateAdded,
    shipsToCountry,
    bestShippingOption,
    isCheckout = false,
    isOfficialStore = false,
    onClick,
}: StorefrontHeaderSummaryProps): React.ReactElement {
    return (
        <VStack spacing="md">
            <VStack spacing="sm">
                <HStack spacing="sm" align="center">
                    <FeedHeaderImage
                        src={(photoUrl || smallPhotoUrl) ?? undefined}
                        designerName={retailerName}
                        size="sm"
                    />
                    <VStack spacing="xxxs">
                        <HStack spacing="xxs" align="center">
                            <Heading textStyle="hero-xs">{retailerName}</Heading>
                            {isCheckout && <VerifiedBadge size="sm" />}
                        </HStack>
                        <div>
                            {canUseMembership() && (
                                <FeedFollowCtaWithDetails
                                    followButton={followButton}
                                    followersCountLabel={followersCountLabel}
                                    designerName={retailerName}
                                />
                            )}
                        </div>
                    </VStack>
                </HStack>
                {(isCheckout || isOfficialStore) && (
                    <HStack spacing="xxs">
                        {isCheckout && <Sticker name="lystShopPartner" />}
                        {isOfficialStore && <Sticker name="officialStore" />}
                    </HStack>
                )}
                <StorefrontDescription description={description} />
                {!!promotions && (
                    <PromotionHeader
                        promotions={promotions}
                        designerName={retailerName}
                        title={retailerName}
                        parentArea="store_modal"
                        onClick={onClick}
                    />
                )}
            </VStack>
            <VStack spacing="sm">
                <StorefrontInfoList
                    dateAdded={dateAdded}
                    shipsToCountry={shipsToCountry}
                    bestShippingOption={bestShippingOption}
                />
                {isCheckout && (
                    <Text textStyle="body-xs" color="secondary">
                        {gettext("storefront.verified.disclaimer")}
                    </Text>
                )}
            </VStack>
        </VStack>
    );
}
